import axios from 'axios';
import { type CacheRequestConfig, setupCache, type AxiosCacheInstance, buildStorage, type BuildStorage } from 'axios-cache-interceptor';
import localforage from 'localforage';
import { type ShapeRequest } from '../model/Shape';
import { type AutocompleteRequest, type ListTripsRequest } from '../types/Autocomplete';
import { type UserChoice } from '../types/Forms';
import { type UnifiedItineraryDTO, type RouteParametersDTO } from '../types/Itinerary';
import { type Stop } from '../model/Stop';

export enum TransportMode {
  FOOT = 'foot',
  BIKE = 'bike',
  CARPOOL = 'carpool',
  TRANSIT = 'transit'
}

// Création d'un stockage persisté avec IndexedDB via localforage
const localForageStorage: BuildStorage = {
  async set(key: string, value: any): Promise<void> {
    await localforage.setItem(key, value);
  },
  async remove(key: string): Promise<void> {
    await localforage.removeItem(key);
  },
  async find(key: string): Promise<any> {
    return await localforage.getItem(key);
  }
};

// On enveloppe localForageStorage avec buildStorage()
const persistentStorage = buildStorage(localForageStorage);

// Création de l'instance Axios avec le cache
const axiosInstance: AxiosCacheInstance = setupCache(axios.create(), {
  storage: persistentStorage, // Utilise le stockage persistant via localforage (IndexedDB)
  ttl: 1000 * 60 * 15, // Durée de vie du cache : 15 minutes
  methods: ['get', 'post'], // Mise en cache pour GET et POST
  generateKey: (request: CacheRequestConfig): string => {
    // Assure toujours le renvoi d'une chaîne non vide
    if (request.method === 'post') {
      // Si l'URL est absente, on retourne une chaîne vide (au risque de provoquer des collisions)
      return request.url !== undefined ? `${request.url}_${JSON.stringify(request.data)}` : '';
    }
    return request.url ?? '';
  }
});

// Classe TripService avec cache persistant
export class TripService {
  async getAll(data: ShapeRequest) {
    return await axios.get(
      `/api/neartrips/${data.min.lat}/${data.min.lng}/${data.max.lat}/${data.max.lng}/${data.minTime}/${data.maxTime}/${data.myTime}`
    );
  }

  async autocomplete(data: AutocompleteRequest) {
    return await axiosInstance.get(`/api/search/autocomplete/${data.lat}/${data.lon}?query=${data.query}`);
  }

  async autocompleteg(data: AutocompleteRequest) {
    return await axiosInstance.get(`/api/search/autocompleteg/${data.lat}/${data.lon}?query=${data.query}`);
  }

  async listTrips(data: ListTripsRequest, mode: TransportMode) {
    // Determine the user's time zone
    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log({ date: new Date(data.date), userTz });
    return (await axiosInstance.get(
      `/api/search/listtrips/${data.fromlat}/${data.fromlon}/${data.tolat}/${data.tolon}/${mode}/${data.timeFactor}/${data.slopeFactor}/${data.safetyFactor}/${data.isPersonalBike}?date=${data.date}&userTz=${userTz}`
    )).data as UnifiedItineraryDTO[];
  }

  async getItinerary(data: RouteParametersDTO) {
    return await axiosInstance.post('/api/itinerary', data);
  }

  async getMyCarItinerary(data: RouteParametersDTO) {
    return await axiosInstance.post('/api/itinerary/itakemycar', data);
  }

  async saveUserTrip(data: UserChoice) {
    return await axiosInstance.post('/api/itinerary/saveusertrip', data);
  }

  async nextStop(stopId: string, stopSequence: number): Promise<Stop[]> {
    const response = await axiosInstance.get(`/api/nextStop/${stopId}/${stopSequence}`);
    return response.data;
  }

  async getRouteFromLocalGraphHopper(
    fromlat: string,
    fromlon: string,
    tolat: string,
    tolon: string,
    profile: string
  ) {
    return await axiosInstance.get(`/api/search/graphopper/${fromlat}/${fromlon}/${tolat}/${tolon}/${profile}`);
  }
}
